import { IElementType, isIElementGenericAnnotation, isIElementImg360, isIElementMarkupImagePoint, isIElementSection } from "@faro-lotv/ielement-types";
import { addIElements, createSignedUrlsForIElementLevelsOfDetail, selectAncestor, selectIElement, selectIElementWorldMatrix, selectIElementWorldPosition } from "@faro-lotv/project-source";
import { selectMarkupMeasurement } from "@pages/project-details/project-markups/sidepanel/preview/select-markup-measurement";

/** Tile data of a panorama, in the shape the lotv `HoloTree` reads it */

/** Everything needed to render the panorama an annotation was placed on */

/**
 * Levels of detail used for the preview, by their tile grid. The 8x4 level is left out, as
 * signing its 32 tiles is not worth it for a preview.
 */
const PREVIEW_LEVELS = [{
  dimX: 1,
  dimY: 1,
  key: "json1x1"
}, {
  dimX: 2,
  dimY: 1,
  key: "json2x1"
}, {
  dimX: 4,
  dimY: 2,
  key: "json4x2"
}];

/** One level of tiles of a panorama, in the shape `HoloTree` accepts: it throws on any other key */

/**
 * Finds the panorama the annotation was placed on. Needs the annotation and its ancestors to be
 * in the store already.
 *
 * @returns undefined if the annotation is not on a panorama with tiles the preview can show
 */
export function findMarkupPanorama({
  markupId,
  projectApiClient,
  signal
}) {
  return async (dispatch, getState) => {
    const panorama = await findPanorama({
      markupId,
      projectApiClient,
      signal,
      state: getState()
    });
    if (!panorama || !hasPreviewTiles(panorama)) {
      return;
    }
    dispatch(addIElements([panorama]));
    return panorama;
  };
}

/**
 * Signs the tiles of the panorama found by `findMarkupPanorama`, and computes where to look at
 * the annotation from.
 */
export function loadMarkupPanorama({
  markupId,
  panorama,
  projectApiClient,
  signal
}) {
  return async (_dispatch, getState) => {
    signal.throwIfAborted();
    const tiles = await getPanoramaTiles(projectApiClient, panorama);
    if (!tiles) {
      return;
    }
    const state = getState();
    return {
      id: panorama.id,
      tiles,
      panoramaMatrix: selectIElementWorldMatrix(panorama.id)(state),
      markupPosition: selectIElementWorldPosition(markupId)(state),
      measurement: selectMarkupMeasurement(markupId)(state)
    };
  };
}

/**
 * Finds the panorama the way the Sphere Viewer does: an annotation placed on an image is inside
 * its panorama, any other one sits next to the panorama of its section.
 */
async function findPanorama({
  markupId,
  projectApiClient,
  signal,
  state
}) {
  const markup = selectIElement(markupId)(state);
  const annotation = selectAncestor(markup, isIElementGenericAnnotation)(state);
  if (annotation && isIElementMarkupImagePoint(annotation)) {
    return selectAncestor(annotation, isIElementImg360)(state);
  }
  const section = selectAncestor(annotation ?? markup, isIElementSection)(state);
  if (!section) {
    return;
  }
  const panoramas = await projectApiClient.getAllIElements({
    parentIds: [section.id],
    types: [IElementType.img360],
    signal
  });
  return panoramas.find(isIElementImg360);
}

/** @returns the tiles of the preview levels of the panorama, or undefined if it has no single-tile level */
async function getPanoramaTiles(projectApiClient, panorama) {
  const levels = panorama.levelsOfDetail?.length ? await getSignedLevels(projectApiClient, panorama) : getLegacyLevels(panorama);
  const jsons = Object.fromEntries(PREVIEW_LEVELS.map(({
    dimX,
    dimY,
    key
  }) => {
    const level = levels.find(level => level.dimX === dimX && level.dimY === dimY);
    return [key, level ? JSON.stringify(level) : ""];
  }));
  if (!jsons.json1x1) {
    return;
  }
  return {
    json1x1: jsons.json1x1,
    json2x1: jsons.json2x1,
    json4x2: jsons.json4x2,
    json8x4: "",
    pixelWidth: panorama.pixelWidth,
    pixelHeight: panorama.pixelHeight,
    uri: panorama.uri,
    // Only read by `extractTransformation`, the world matrix of the element is used instead
    pose: {
      rot: {
        x: 0,
        y: 0,
        z: 0,
        w: 1
      }
    }
  };
}

/** @returns the preview levels of the panorama, with their tiles signed */
async function getSignedLevels(projectApiClient, panorama) {
  const signedUrls = createSignedUrlsForIElementLevelsOfDetail(projectApiClient, panorama) ?? [];
  const levels = await Promise.all((panorama.levelsOfDetail ?? []).map(async ({
    dimX,
    dimY
  }, index) => {
    const urls = signedUrls.at(index);
    if (!urls || !isPreviewLevel(dimX, dimY)) {
      return;
    }
    const sources = await Promise.all(urls.map(url => url.requestSignedUrl()));
    return {
      dimX,
      dimY,
      sources
    };
  }));
  return levels.filter(level => !!level);
}

/** @returns the levels of an older panorama, which lists its tiles in the `json*` fields instead */
function getLegacyLevels(panorama) {
  return [panorama.json1x1, panorama.json2x1, panorama.json4x2].flatMap(json => {
    const level = json ? parseLegacyLevel(json) : undefined;
    return level ? [level] : [];
  });
}

/** @returns whether the panorama has the single-tile level the preview needs, without signing anything */
function hasPreviewTiles(panorama) {
  if (panorama.levelsOfDetail?.length) {
    return panorama.levelsOfDetail.some(({
      dimX,
      dimY
    }) => dimX === 1 && dimY === 1);
  }
  return !!parseLegacyLevel(panorama.json1x1);
}

/** Some of these fields are not valid JSON, with unquoted keys and sources */
function parseLegacyLevel(json) {
  const dimX = /dimX"?\s*:\s*(\d+)/.exec(json)?.[1];
  const dimY = /dimY"?\s*:\s*(\d+)/.exec(json)?.[1];
  const sources = /sources"?\s*:\s*\[([^\]]*)\]/.exec(json)?.[1];
  if (!dimX || !dimY || !sources) {
    return;
  }
  return {
    dimX: Number(dimX),
    dimY: Number(dimY),
    sources: sources.split(",").map(source => source.trim().replace(/^"|"$/g, "")).filter(source => !!source)
  };
}
function isPreviewLevel(dimX, dimY) {
  return PREVIEW_LEVELS.some(level => level.dimX === dimX && level.dimY === dimY);
}